import useGetPrescription from 'Hooks/teleconsult/useGetPrescription';
import Space from 'Modules/Space';
import { removeModal } from 'Redux/modules/modal/actions';
import { shape, string } from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Segment } from 'semantic-ui-react';
import { prescriptionResetState } from 'Redux/modules/webChat/actions';
import moment from 'moment';
import { PHARMACY_SOURCE_LIFEPACK } from 'Constants/consults';

import css from './Prescription.less';
import WarningMessage from './WarningMessage';
import LifepackMessage from './LifepackMessage';

import LoadingItems from '@/components/LoadingItems';
import PrescriptionItem from '@/components/PrescriptionItem/PrescriptionItem';
import PriceSummary from '@/components/PriceSummary';

const Prescription = ({ data, id }) => {
  const { prescription, loading, error, getPrescriptionDetails } = useGetPrescription();

  const {
    medicines = [],
    note,
    order,
    expiresAt,
    status,
    currency,
    practiceCost,
    lifepackPrescriptionStatus,
    lifepackCancellationReason,
    source,
  } = prescription ?? {};

  const { subTotal, total, deliveryFee, loyaltyRedemptionAmount } = order?.fee ?? prescription;

  const orderItemIds = order?.medicines?.map(item => item.id) ?? [];

  const dispatch = useDispatch();

  const onClose = useCallback(() => {
    dispatch(removeModal(id));
    dispatch(prescriptionResetState());
  }, [dispatch, id]);

  useEffect(() => {
    getPrescriptionDetails(data.prescriptionId);
  }, [data.prescriptionId, getPrescriptionDetails]);

  return (
    <div className={css.container}>
      <Modal onClose={onClose} open closeIcon className={css.customModal} size="small">
        <Modal.Header>{error ? 'Error' : 'Prescription'}</Modal.Header>
        <Modal.Content className={css.modalContent}>
          {loading ? (
            <LoadingItems />
          ) : (
            <>
              {status === 'pending' && expiresAt && (
                <WarningMessage
                  message={`Available to order till ${moment(expiresAt).format(
                    'MMM DD, YYYY HH:mm',
                  )}`}
                />
              )}

              {source === PHARMACY_SOURCE_LIFEPACK && (
                <LifepackMessage
                  lifepackPrescriptionStatus={lifepackPrescriptionStatus}
                  lifepackCancellationReason={lifepackCancellationReason}
                />
              )}

              {error ? (
                <div>{error?.message}</div>
              ) : (
                <>
                  <div className="font-semibold" style={{ fontSize: 16 }}>
                    Medicines
                  </div>
                  <Space gap={0} width="100%">
                    {medicines?.map(medicine => (
                      <PrescriptionItem
                        disabled={orderItemIds.length > 0 && !orderItemIds.includes(medicine.id)}
                        key={medicine.id}
                        medicine={medicine}
                      />
                    ))}
                  </Space>
                  <PriceSummary
                    total={total}
                    subTotal={subTotal}
                    deliveryFee={deliveryFee}
                    loyaltyAmount={loyaltyRedemptionAmount}
                    currency={currency}
                    practiceCost={practiceCost}
                  />

                  {note && (
                    <>
                      <div className="font-semibold mt-1" style={{ fontSize: 16 }}>
                        Important Note
                      </div>
                      <Segment style={{ width: '100%', fontSize: 16 }}>{note}</Segment>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
};

export default Prescription;

Prescription.propTypes = {
  data: shape({}).isRequired,
  id: string.isRequired,
};
